/**
 * WikiHubData.ts
 *
 * Types and section data for the Wiki home page (WikiHome.tsx).
 * Edit this file to update the guide cards shown on the wiki landing page.
 */

import type { FC, SVGProps } from "react";
import SubjectsIcon from "../../assets/icons/subjects.svg?react";
import SitesIcon from "../../assets/icons/sites.svg?react";
import EnrollmentIcon from "../../assets/icons/enrollment.svg?react";
import SafetyIcon from "../../assets/icons/safety.svg?react";
import ClinicalIcon from "../../assets/icons/clinical.svg?react";
import ShieldLockedIcon from "../../assets/icons/shield_locked.svg?react";

export type CardType = "article" | "video" | "walkthrough";
export type ImageryType = "icon" | "image" | "screenshot";
export type AccentIdx = 0 | 1 | 2 | 3;

export type HubCardData = {
  id: string;
  title: string;
  description?: string;
  type: CardType;
  path?: string;
  readTime?: string;
  duration?: string;
  videoSrc?: string;
  isNew?: boolean;
  imagery: ImageryType;
  icon?: FC<SVGProps<SVGSVGElement>>;
  imageSrc?: string;
  accent?: AccentIdx;
};

export type HubSectionData = {
  key: string;
  eyebrow: string;
  title: string;
  cards: HubCardData[];
};

export type LatestRelease = {
  version: string;
  date: string;
  description: string;
};

export const ACCENT_GRADIENTS: Record<AccentIdx, string> = {
  0: "linear-gradient(135deg, #d2eff9 0%, #a4daed 100%)",
  1: "linear-gradient(135deg, #e8f4f0 0%, #c5e4d8 100%)",
  2: "linear-gradient(135deg, #efe6f4 0%, #d4b8e0 100%)",
  3: "linear-gradient(135deg, #fce8d8 0%, #f2c9ae 100%)",
};

export const HUB_SECTIONS: HubSectionData[] = [
  {
    key: "basics",
    eyebrow: "Basics",
    title: "Getting Started",
    cards: [
      {
        id: "what-is-vivo",
        title: "What is Vivo",
        description:
          "Vivo helps users explore trial data, monitor important signals, and answer questions more efficiently in one place.",
        type: "article",
        readTime: "2 min",
        videoSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/getting-started-what-is-vivo.mp4",
        imagery: "image",
        path: "basics-what-is-vivo",
      },
      {
        id: "navigating-vivo",
        title: "Navigating Vivo",
        description:
          "Learn how Vivo is organized and the navigation patterns that repeat across every screen.",
        type: "video",
        duration: "4:00",
        imagery: "image",
        imageSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/getting-started-navigating-vivo.png",
        path: "basics-navigating-vivo",
      },
      {
        id: "screen-types",
        title: "Vivo screen types",
        description:
          "Understand how Vivo presents data — Dashboard Studio screens, purpose-built views, and specialized dashboard features like Timeline, Metrics, and Map.",
        type: "walkthrough",
        readTime: "5 min",
        imagery: "image",
        imageSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/exploration-special-dashboard-card.png",
        path: "basics-screen-types",
      },
    ],
  },
  {
    key: "inquiry",
    eyebrow: "Inquiry",
    title: "Ask, Discover, Act",
    cards: [
      {
        id: "askvivo-overview",
        title: "Ask Vivo",
        description:
          "Ask Vivo is your AI-powered assistant designed to help you explore and analyze your clinical trial data through a conversational interface. Simply ask your questions in plain language.",
        type: "video",
        duration: "4:00",
        videoSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/inquiry-askvivo-card.mp4",
        imagery: "image",
        isNew: true,
        path: "inquiry-askvivo",
      },
      {
        id: "insights-101",
        title: "Insights",
        description:
          "Create personalized, auto-updating snapshots derived from Ask Vivo questions. Monitor the metrics that matter most and stay ahead of your trial's key trends.",
        type: "video",
        duration: "4:00",
        imagery: "image",
        imageSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/inquiry-insights-card.png",
        path: "insights-enhanced",
      },
      {
        id: "alerts",
        title: "Alerts",
        description:
          "Let Vivo continuously monitor trial data across all sources and notify you when trends move in the wrong direction.",
        type: "walkthrough",
        readTime: "4 min",
        imagery: "image",
        imageSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/inquiry-alerts-main-screen-card.png",
        path: "alerts-enhanced",
      },
      {
        id: "user-guidelines",
        title: "User guidelines",
        description:
          "As a user of Ask Vivo, you play a crucial role in ensuring responsible and effective use of AI-driven capabilities.",
        type: "article",
        readTime: "3 min",
        imagery: "image",
        imageSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/inquiry-userguidelines-card.png",
        path: "inquiry-askvivo-user-guidelines",
      },
    ],
  },
  {
    key: "exploration",
    eyebrow: "Exploration",
    title: "Shape Your View",
    cards: [
      {
        id: "dashboard-studio-overview",
        title: "Dashboard Studio",
        description:
          "Learn what workspaces are, why Table and Dashboard tabs work together, and how Dashboard Studio helps you explore trial data.",
        type: "walkthrough",
        readTime: "5 min",
        videoSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/exploration-dashboard-studio-gif.mp4",
        imagery: "image",
        isNew: true,
        path: "exploration-dashboard-studio-overview",
      },
      {
        id: "table-tab-essentials",
        title: "Tables",
        description:
          "Learn how to filter, sort, choose columns, save your setup, and export from the Table tab in Dashboard Studio.",
        type: "walkthrough",
        readTime: "9 min",
        imagery: "image",
        imageSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/exploration-table-card.png",
        path: "exploration-table-tab",
      },
      {
        id: "dashboards",
        title: "Dashboards",
        description:
          "Learn how the Dashboard tab works, how it connects to your table filters, and how to manage widgets without needing plot-builder expertise.",
        type: "walkthrough",
        readTime: "5 min",
        imagery: "image",
        imageSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/exploration-dashboard-card.png",
        path: "exploration-dashboard-tab",
      },
      {
        id: "plots",
        title: "Plot Builder",
        description:
          "Build, refine, and manage visualizations in Dashboard Studio. Learn how widget filters, plot settings, and layout tools work together.",
        type: "walkthrough",
        readTime: "13 min",
        imagery: "image",
        imageSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/exploration-plot-builder-card.png",
        path: "exploration-plot-builder",
      },
      {
        id: "workspaces-deep",
        title: "Workspaces",
        description:
          "Save filters, columns, and dashboards as a single switchable view. The pattern that powers every list page in Vivo.",
        type: "walkthrough",
        readTime: "5 min",
        imagery: "image",
        imageSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/exploration-workspaces-panel.png",
        path: "exploration-workspaces",
      },
    ],
  },
  {
    key: "domains",
    eyebrow: "Domains",
    title: "Trial Data, Organized",
    cards: [
      {
        id: "sites",
        title: "Sites",
        description:
          "Monitor enrollment performance, safety signals, and operational metrics across every clinical site in your trial.",
        type: "article",
        readTime: "4 min",
        imagery: "icon",
        icon: SitesIcon,
        accent: 2,
        path: "exploration-sites-overview",
      },
      {
        id: "enrollment",
        title: "Enrollment",
        description:
          "Track enrollment against your study's targets, from a single site to the entire trial — rates, trends, and forecast scenarios.",
        type: "article",
        readTime: "3 min",
        imagery: "icon",
        icon: EnrollmentIcon,
        accent: 0,
        path: "exploration-trial-enrollment",
      },
      {
        id: "subjects",
        title: "Participant Profiles",
        description:
          "Explore demographics, enrollment status, screening outcomes, and study results across all participants in your trial.",
        type: "article",
        readTime: "3 min",
        imagery: "icon",
        icon: SubjectsIcon,
        accent: 1,
        path: "subjects-overview",
      },
      {
        id: "single-subject",
        title: "Subject Profiles",
        description:
          "See every visit, activity, AE, dose, lab result, and assessment for a single participant — all in one place.",
        type: "article",
        readTime: "3 min",
        imagery: "icon",
        icon: SubjectsIcon,
        accent: 0,
        path: "exploration-singlesubject-schedule",
      },
      {
        id: "safety",
        title: "Safety",
        description:
          "Monitor adverse events, IP administration, and pharmacokinetics across all participants — with full causality, severity, and dose detail.",
        type: "article",
        readTime: "3 min",
        imagery: "icon",
        icon: SafetyIcon,
        accent: 3,
        path: "exploration-safety-adverse-events",
      },
      {
        id: "measures",
        title: "Measures",
        description:
          "Explore assessments, imaging, labs, and questionnaires — with field references and reference ranges for every data type.",
        type: "article",
        readTime: "3 min",
        imagery: "icon",
        icon: ClinicalIcon,
        accent: 0,
        path: "exploration-measures-assessments",
      },
    ],
  },
  {
    key: "data",
    eyebrow: "Data",
    title: "Connected and Checked",
    cards: [
      {
        id: "data-config",
        title: "Configuration",
        description:
          "See where your trial data comes from, when it was last updated, and whether each connection is currently healthy.",
        type: "article",
        readTime: "3 min",
        imagery: "image",
        imageSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/data-configuration-connections-card.png",
        path: "data-connections",
      },
      {
        id: "data-discrepancies",
        title: "Discrepancies",
        description:
          "Review, triage, and track data discrepancies automatically identified by Vivo across all connected sources.",
        type: "walkthrough",
        readTime: "8 min",
        videoSrc:
          "https://storage.googleapis.com/omniscience-vivo-public/wiki/data-discrepancies.mp4",
        imagery: "image",
        path: "data-discrepancies",
      },
    ],
  },
  {
    key: "admin",
    eyebrow: "Admin",
    title: "Manage Your Organization",
    cards: [
      {
        id: "admin-portal",
        title: "Client Admin Portal",
        description:
          "Get the full overview of admin access, SSO boundaries, user management, permissions, and activity monitoring.",
        type: "walkthrough",
        readTime: "6 min",
        imagery: "icon",
        icon: ShieldLockedIcon,
        accent: 3,
        isNew: true,
        path: "admin-portal",
      },
      {
        id: "admin-users-access",
        title: "Users & Access",
        description:
          "Invite users, choose account types, and manage each person’s admin settings from the portal.",
        type: "article",
        readTime: "3 min",
        imagery: "icon",
        icon: ShieldLockedIcon,
        accent: 0,
        path: "admin-users-access",
      },
      {
        id: "admin-roles-permissions",
        title: "Roles & Permissions",
        description:
          "Review study-level assignments, edit access, and download the audit trail for trial permissions.",
        type: "article",
        readTime: "3 min",
        imagery: "icon",
        icon: ShieldLockedIcon,
        accent: 1,
        path: "admin-roles-permissions",
      },
      {
        id: "admin-activity-log",
        title: "Activity Log",
        description:
          "Track Ask Vivo usage, page views, and login activity across the current tenant workspace.",
        type: "article",
        readTime: "2 min",
        imagery: "icon",
        icon: ShieldLockedIcon,
        accent: 2,
        path: "admin-activity-log",
      },
    ],
  },
];
